import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Icon from "./Icon";
import { BENEFIT_SYSTEMS } from "../data/content";
import { INGREDIENT_GROUPS } from "../data/ingredients";

// In-depth, expandable benefit systems with a dark-reveal animation.
export const BenefitsExplorer = () => {
  const [open, setOpen] = useState(0);

  return (
    <div className="space-y-3" data-testid="benefits-explorer">
      {BENEFIT_SYSTEMS.map((b, i) => {
        const group = INGREDIENT_GROUPS[b.group];
        const isOpen = open === i;
        const actives = group.ingredients.slice(0, 4);
        return (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`relative rounded-3xl border overflow-hidden transition-all duration-500 ${
              isOpen ? "bg-navy-900 border-navy-900 shadow-2xl shadow-navy-900/20" : "bg-white border-slate-100 hover:border-slate-200"
            }`}
          >
            <button
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="w-full flex items-center gap-5 p-5 md:p-7 text-left"
              data-testid={`benefit-system-${i}`}
            >
              <span
                className={`flex items-center justify-center w-14 h-14 rounded-2xl shrink-0 transition-all duration-500 ${
                  isOpen ? "bg-white/10 text-white scale-110" : `${group.bg} ${group.fg}`
                }`}
              >
                <Icon name={b.icon} className="w-7 h-7" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className={`font-heading text-xl md:text-2xl font-semibold transition-colors ${isOpen ? "text-white" : "text-navy-900"}`}>
                  {b.title}
                </h3>
                <p className={`text-sm mt-0.5 transition-colors ${isOpen ? "text-slate-300" : "text-slate-400"}`}>{b.tagline}</p>
              </div>
              <motion.span
                animate={{ rotate: isOpen ? 135 : 0 }}
                transition={{ duration: 0.4 }}
                className={`shrink-0 ${isOpen ? "text-white" : "text-slate-400"}`}
              >
                <Plus className="w-6 h-6" />
              </motion.span>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-5 md:px-7 pb-8 grid md:grid-cols-2 gap-8">
                    <div>
                      <p className="text-slate-300 leading-relaxed">{b.detail}</p>
                      <Link to="/ingredients" className="mt-5 inline-flex items-center gap-2 text-sky-300 font-semibold text-sm hover:gap-3 transition-all">
                        See the full formula <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    <div>
                      <p className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-4">Key Actives</p>
                      <div className="space-y-2">
                        {actives.map((ing, j) => (
                          <motion.div
                            key={ing.name}
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 + j * 0.07 }}
                            className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-300 shrink-0" />
                            <span className="text-sm text-white font-medium">{ing.name}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export default BenefitsExplorer;
